import { Immutable } from "immer";
import React from "react";
import { PromoConditionalBlock } from "tsi-common-react/src/components/PromoConditionalBlock/index";
import { fromSSRData } from "tsi-common-react/src/utils/ssr";

import { templates } from "@reactivated";
import type {
    HardcodedBlock as HardcodedBlockValue,
    HotSpotBlock as HotSpotBlockValue,
    TempurpedicPromoConditionalBlock as PromoConditionalBlockValue,
    ReviewsBlock as ReviewsBlockValue,
    TabsBlock as TabsBlockValue,
} from "@reactivated";

import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { HardcodedBlock } from "../components/HardcodedBlock";
import { HotSpotBlock } from "../components/HotSpotBlock";
import { ReviewsBlock } from "../components/ReviewsBlock";
import { Template as BreadcrumbsBlock } from "../templates/BreadcrumbsBlockTemplate";
import { Template as CustomerServiceInfoBlock } from "../templates/CustomerServiceInfoBlockTemplate";
import { Template as ShopCompareGridBlock } from "../templates/ShopCompareGridTemplate";
import { Template as TabFeatureAttributeBlock } from "../templates/TabFeatureAttributeBlockTemplate";
import { TabsBlock } from "./TabsBlock";
import { toolboxComponentsMap } from "./toolboxComponentsMap";

/**
 * Shared builder for the consolidated Toolbox PAGE-BODY component maps (#33588;
 * BasicPage / UtilityPageWithToolbox / ProductListFilterPage, and any later
 * Toolbox page).
 *
 * Spreads the shared ``toolboxComponentsMap`` foundation, then layers the four
 * page/request/settings-dependent block overrides (``hardcoded_block`` /
 * ``hotspot_block`` / ``breadcrumbs_block`` / ``customer_service_info_block``)
 * keyed by ``block.id`` against the ``get_toolbox_block_ssr`` side-channels.
 *
 * CRITICAL (the nested-override propagation fix): the base map's recursive
 * container entries (``tabs_block`` panels + ``promo_conditional_block``
 * content) dispatch their nested ``ToolboxContent`` through the BASE map -- whose
 * side-channel entries carry EMPTY props (``application_source=""`` etc.). A
 * plain ``{ ...toolboxComponentsMap, hardcoded_block: <override> }`` spread copies
 * the base container FUNCTION REFERENCES, whose closures still point at the base
 * map, so a side-channel block nested inside a tab panel / promo content would
 * render with empty props -- a refactor regression vs the legacy
 * ``{% include_block %}`` path (which rendered nested blocks WITH page context;
 * the dead-context trap, af30dca2bc). To fix it, this builder RE-defines the two
 * container entries so they dispatch nested content through THIS page map
 * (self-referential, captured via a ``let`` the entries close over) -- mirroring
 * the base map's container markup exactly (the per-child
 * ``<div class="w-block-{type} block-{type}">`` wrappers Wagtail ``render_basic``
 * emits), but with the page-level side-channel overrides in scope at every depth.
 *
 * Page-specific extra keys (the Basic/Utility ``heading`` / ``paragraph`` /
 * ``image`` / ``html`` primitives) are spread by the CALLER on top of the
 * returned map. That is safe: those keys live only on the top-level page
 * StreamBlock (``BasicPageContent`` / ``UtilityPageBodyContent``), NOT in
 * ``ToolboxContent``, so they can never appear nested inside a tab panel /
 * promo content -- the container entries never need them.
 */
/**
 * The four side-channel dicts every Toolbox page body threads (all three page
 * bodies share the identical shape). Typed against the generated
 * ``BasicPageTemplate`` props so the dict value types
 * (``BreadcrumbsBlockTemplate`` / ``CustomerServiceInfoBlockTemplate``) stay in
 * lockstep with reactivated codegen.
 */
export type ToolboxPageSSR = Pick<
    templates.BasicPageTemplate,
    | "hardcoded_ssr"
    | "hotspot_ssr"
    | "breadcrumbs_ssr"
    | "customer_service_ssr"
    | "shop_compare_ssr"
    | "tab_feature_attribute_ssr"
    | "reviews_ssr"
>;

export const buildToolboxPageMap = (ssr: Immutable<ToolboxPageSSR>) => {
    // Self-reference: the container entries close over `map` so nested
    // ToolboxContent dispatches through the PAGE map (with side-channel
    // overrides), not the base map.
    const map = {
        ...toolboxComponentsMap,

        // `hardcoded_block`: side-channel `application_source` (legacy
        // `page.slug`), keyed by block.id. Required by the interactive
        // `hc-block--prequal-card`.
        hardcoded_block: (block: {
            id?: string;
            value: Immutable<HardcodedBlockValue>;
        }) => (
            <HardcodedBlock
                value={block.value}
                application_source={
                    (block.id ? ssr.hardcoded_ssr[block.id] : "") ?? ""
                }
            />
        ),

        // `hotspot_block`: side-channel `variant` (legacy `hotspot_variant`).
        hotspot_block: (block: {
            id?: string;
            value: Immutable<HotSpotBlockValue>;
        }) => (
            <HotSpotBlock
                value={block.value}
                variant={(block.id ? ssr.hotspot_ssr[block.id] : "") ?? ""}
            />
        ),

        // `breadcrumbs_block` / `customer_service_info_block`: StaticBlocks with
        // no serializable value; ALL data is page/settings-derived, looked up by
        // block.id from the side-channel.
        breadcrumbs_block: (block: { id?: string }) => {
            const bc = block.id ? ssr.breadcrumbs_ssr[block.id] : undefined;
            return (
                <BreadcrumbsBlock
                    ancestors={bc?.ancestors ?? []}
                    current_title={bc?.current_title ?? ""}
                />
            );
        },
        customer_service_info_block: (block: { id?: string }) => {
            const cs = block.id
                ? ssr.customer_service_ssr[block.id]
                : undefined;
            return (
                <CustomerServiceInfoBlock
                    chat_offline_link={cs?.chat_offline_link ?? null}
                    pre_order_phone_rfc3966={cs?.pre_order_phone_rfc3966 ?? ""}
                    pre_order_phone_national={
                        cs?.pre_order_phone_national ?? ""
                    }
                    talk_modal_hours_top_html={
                        cs?.talk_modal_hours_top_html ?? ""
                    }
                    customer_service_phone_rfc3966={
                        cs?.customer_service_phone_rfc3966 ?? ""
                    }
                    customer_service_phone_national={
                        cs?.customer_service_phone_national ?? ""
                    }
                    talk_modal_hours_bottom_html={
                        cs?.talk_modal_hours_bottom_html ?? ""
                    }
                />
            );
        },

        // `shop_compare_grid` / `tab_feature_attribute_block`: the base map
        // renders these `null` because their native serialized block value does
        // NOT match the server-FLATTENED envelope their standalone Templates
        // consume. The page body computes that envelope server-side
        // (`get_toolbox_block_ssr`, keyed by block.id) and threads it here, so
        // the real component renders instead of nothing (latent-regression fix:
        // both are ToolboxContent blocks, so an editor can place them on ANY
        // toolbox page). Render null only when the side-channel has no envelope
        // for this block id (defensive -- the server walk covers every block).
        shop_compare_grid: (block: { id?: string }) => {
            const env = block.id ? ssr.shop_compare_ssr[block.id] : undefined;
            if (!env) {
                return null;
            }
            // Bridge the deeply-readonly SSR envelope to the shared
            // ShopCompareGrid Template's mutable reactivated prop type via the
            // sanctioned SSR-boundary helper (the shared re-export does not
            // accept `Immutable<>`).
            return (
                <ShopCompareGridBlock
                    {...fromSSRData<templates.ShopCompareGridTemplate>(env)}
                />
            );
        },
        tab_feature_attribute_block: (block: { id?: string }) => {
            const cms = block.id
                ? ssr.tab_feature_attribute_ssr[block.id]
                : undefined;
            return cms ? <TabFeatureAttributeBlock cms={cms} /> : null;
        },

        // `reviews` (ReviewsBlock): the base map renders ONLY the inner review
        // carousel, dropping the whole `.reviews-block` wrapper -- the wrapper
        // now lives in `ReviewsBlock`, which needs the page-derived
        // `reviews_url` (the stars-theme rating link) and `brand_color` (the
        // rating's class). Both are looked up by block.id from the side-channel
        // (legacy `reviewspage` / `page.brand_color`), defaulting to "" when the
        // side-channel has no entry (#33456 wrapper-drop fix).
        reviews: (block: {
            id?: string;
            value: Immutable<ReviewsBlockValue>;
        }) => {
            const rv = block.id ? ssr.reviews_ssr[block.id] : undefined;
            return (
                <ReviewsBlock
                    value={block.value}
                    reviews_url={rv?.reviews_url ?? ""}
                    brand_color={rv?.brand_color ?? ""}
                />
            );
        },

        // --- RECURSIVE containers re-pointed at THIS page map ---
        // Reproduce the base map's container markup (same per-child
        // `<div class="w-block-{type} block-{type}">` Wagtail render_basic
        // wrappers) but dispatch nested ToolboxContent through `map` so the
        // page-level side-channel overrides apply at every nesting depth.
        tabs_block: (block: { value: Immutable<TabsBlockValue> }) => (
            <TabsBlock
                value={block.value}
                renderPanel={(panel) =>
                    panel.map((child) => (
                        <StreamField
                            key={child.id}
                            tagName="div"
                            attrs={{
                                className: `w-block-${child.type} block-${child.type}`,
                            }}
                            value={[child]}
                            components={map}
                        />
                    ))
                }
            />
        ),
        promo_conditional_block: (block: {
            value: Immutable<PromoConditionalBlockValue>;
        }) => (
            <PromoConditionalBlock value={block.value}>
                {(block.value.content ?? []).map((child) => (
                    <StreamField
                        key={child.id}
                        tagName="div"
                        attrs={{
                            className: `w-block-${child.type} block-${child.type}`,
                        }}
                        value={[child]}
                        components={map}
                    />
                ))}
            </PromoConditionalBlock>
        ),
    };
    return map;
};
